import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ReportOeePerformance.module.css";

export interface OeeGaugeProps {
  /** OEE de produção em % (cru; pode ser > 100 se produzir acima da meta). */
  valuePct: number;
  /** Limite (%) que separa a zona vermelha da verde. */
  threshold?: number;
  labels: { oee: string; statusGood: string; statusBad: string };
  locale: string;
}

// ---- Geometria do semicírculo (0 à esquerda = 180°, 100 à direita = 0°) ----
const CX = 170;
const CY = 168;
const R = 130;
const SW = 22;
const VB_W = 340;
const VB_H = 200;
const ANIM_MS = 950;

const polar = (radius: number, v: number): [number, number] => {
  const th = Math.PI * (1 - v / 100); // v=0 → π (esquerda), v=100 → 0 (direita)
  return [CX + radius * Math.cos(th), CY - radius * Math.sin(th)];
};
const f = (n: number): string => n.toFixed(2);

/**
 * Arco entre dois valores no MESMO círculo (raio R). Como o mostrador inteiro é um
 * semicírculo (0–100 = 180°), nenhum sub-arco passa de 180° → large-arc-flag é sempre 0.
 * sweep=1 mantém o traçado por cima (sentido horário na tela).
 */
const arcPath = (v1: number, v2: number): string => {
  const [x1, y1] = polar(R, v1);
  const [x2, y2] = polar(R, v2);
  return `M ${f(x1)} ${f(y1)} A ${R} ${R} 0 0 1 ${f(x2)} ${f(y2)}`;
};

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

const OeeGauge: React.FC<OeeGaugeProps> = ({ valuePct, threshold = 85, labels, locale }) => {
  // Valor animado: entra de 0 e varre até o valor real (como os gráficos Chart.js do sistema).
  const [animPct, setAnimPct] = useState<number>(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const target = Number.isFinite(valuePct) ? valuePct : 0;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setAnimPct(target);
      return;
    }

    const from = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const p = Math.min(1, (now - start) / ANIM_MS);
      setAnimPct(from + (target - from) * easeOutCubic(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [valuePct]);

  // Status/cor ancoram no valor FINAL (sem piscar durante a varredura); agulha e número seguem o animado.
  const good = valuePct >= threshold;
  const valueColor = good
    ? "var(--color-feedback-success-700, #16a34a)"
    : "var(--color-feedback-danger-700, #dc2626)";
  const needleV = Math.max(0, Math.min(100, animPct)); // a agulha nunca ultrapassa o arco

  const geo = useMemo(() => {
    const [tInX, tInY] = polar(R - SW / 2 - 2, threshold);
    const [tOutX, tOutY] = polar(R + SW / 2 + 2, threshold);
    const [tLabX, tLabY] = polar(R + SW / 2 + 15, threshold);
    const [e0X, e0Y] = polar(R, 0);
    const [e100X, e100Y] = polar(R, 100);
    const [ndX, ndY] = polar(R - SW / 2 - 12, needleV);

    // Eixo do gradiente na TANGENTE do ponto do limite → fade reto (radial), curto. Vermelho antes, verde depois.
    const thT = Math.PI * (1 - threshold / 100);
    const tanX = Math.sin(thT);
    const tanY = Math.cos(thT);
    const [pThrX, pThrY] = polar(R, threshold);
    const FADE = 6;

    return {
      trackPath: arcPath(0, 100),
      gx1: pThrX - FADE * tanX, gy1: pThrY - FADE * tanY, // lado vermelho (v < limite)
      gx2: pThrX + FADE * tanX, gy2: pThrY + FADE * tanY, // lado verde (v > limite)
      tInX, tInY, tOutX, tOutY,
      tLabX, tLabY: tLabY + 4,
      e0X, e0Y: e0Y + 20, e100X, e100Y: e100Y + 20,
      ndX, ndY,
    };
  }, [threshold, needleV]);

  // Exibição limitada a 100% no máximo (produção acima da meta não estoura o mostrador).
  const displayPct = Math.min(100, animPct);
  const valueText = displayPct.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <div className={styles.gaugeBox}>
      <svg
        className={styles.gaugeSvg}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${Math.min(100, valuePct).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% — ${good ? labels.statusGood : labels.statusBad}`}
      >
        {/* Faixa única com fade curto e reto no limite (gradiente com eixo na tangente do ponto do limite). */}
        <defs>
          <linearGradient
            id="oeeZoneGrad"
            gradientUnits="userSpaceOnUse"
            x1={f(geo.gx1)}
            y1={f(geo.gy1)}
            x2={f(geo.gx2)}
            y2={f(geo.gy2)}
            spreadMethod="pad"
          >
            <stop offset="0" stopColor="var(--color-feedback-danger-500, #ef4444)" />
            <stop offset="1" stopColor="var(--color-feedback-success-500, #22c55e)" />
          </linearGradient>
        </defs>
        <path d={geo.trackPath} fill="none" stroke="url(#oeeZoneGrad)" strokeWidth={SW} strokeLinecap="round" />

        {/* Marca do limite */}
        <line x1={f(geo.tInX)} y1={f(geo.tInY)} x2={f(geo.tOutX)} y2={f(geo.tOutY)} stroke="var(--color-ui-text-primary, #111827)" strokeWidth={2.5} strokeLinecap="round" />
        <text x={f(geo.tLabX)} y={f(geo.tLabY)} textAnchor="middle" fontSize="12" fontWeight={700} fill="var(--color-ui-text-primary, #111827)">
          {threshold}%
        </text>

        {/* Rótulos das pontas */}
        <text x={f(geo.e0X)} y={f(geo.e0Y)} textAnchor="middle" fontSize="11" fill="var(--color-ui-text-secondary, #6b7280)">0</text>
        <text x={f(geo.e100X)} y={f(geo.e100Y)} textAnchor="middle" fontSize="11" fill="var(--color-ui-text-secondary, #6b7280)">100</text>

        {/* Agulha + hub */}
        <line x1={CX} y1={CY} x2={f(geo.ndX)} y2={f(geo.ndY)} stroke={valueColor} strokeWidth={5} strokeLinecap="round" />
        <circle cx={CX} cy={CY} r={9} fill="var(--color-ui-bg-surface, #ffffff)" stroke={valueColor} strokeWidth={3.5} />
      </svg>

      <div className={styles.gaugeReadout}>
        <div className={styles.gaugeValueRow}>
          <span className={styles.gaugeValue} style={{ color: valueColor }}>{valueText}</span>
          <span className={styles.gaugePct} style={{ color: valueColor }}>%</span>
        </div>
        <div className={styles.gaugeOeeLabel}>{labels.oee}</div>
        <div className={`${styles.gaugeStatus} ${good ? styles.gaugeStatusGood : styles.gaugeStatusBad}`}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
            {good ? "check_circle" : "error"}
          </span>
          {good ? labels.statusGood : labels.statusBad}
        </div>
      </div>
    </div>
  );
};

export default OeeGauge;
